import React, { useState, useRef, useEffect } from 'react';

/**
 * AdminTableToolbar
 *
 * Top toolbar for admin data tables.
 *
 * Layout when jsxFilters is provided:
 *   Row 1: [Search ···············] [Columns ▾] [+ Create] [↺]
 *   Row 2: [jsxFilters — chips / selects ···················]
 *
 * Without jsxFilters, a single-row layout is used.
 *
 * Props:
 *   searchValue        {string}    current search string
 *   onSearchChange     {fn}        called with new search string
 *   searchPlaceholder  {string}    placeholder for search input
 *   jsxFilters         {ReactNode} filter controls (chips, selects…)
 *   columns            {Array}     [{ key, title, visible }] toggleable columns
 *   onToggleColumn     {fn}        called with column key when toggled
 *   onShowAllColumns   {fn}        marks all columns visible
 *   onHideAllColumns   {fn}        marks all columns hidden
 *   onResetColumns     {fn}        resets visibility defaults
 *   onCreateClick      {fn}        called when "Create" button clicked
 *   onRefreshClick     {fn}        called when refresh button clicked
 */
const AdminTableToolbar = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = '',
  jsxFilters,
  jsxSearchRowExtra,
  columns,
  onToggleColumn,
  onShowAllColumns,
  onHideAllColumns,
  onResetColumns,
  onCreateClick,
  onRefreshClick,
  // Optional secondary "Create (New UX)" button
  onCreateAltClick,
  createAltLabel,
}) => {
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Close column menu when clicking outside
  useEffect(() => {
    if (!showColumnMenu) return;
    const handleOutsideClick = (e) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showColumnMenu]);

  const hasColumns = Array.isArray(columns) && columns.length > 0;
  const visibleCount = hasColumns ? columns.filter(c => c.visible !== false).length : 0;
  const totalCount = hasColumns ? columns.length : 0;
  const twoRows = Boolean(jsxFilters);

  const jsxSearch = (
    <div className="adm-table-toolbar__search-wrap">
      <input
        type="text"
        className="adm-table-toolbar__search-input"
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={e => typeof onSearchChange === 'function' && onSearchChange(e.target.value)}
      />
      {searchValue && (
        <button
          className="adm-table-toolbar__search-clear"
          onClick={() => typeof onSearchChange === 'function' && onSearchChange('')}
          title="Clear search"
          type="button"
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      )}
    </div>
  );

  const jsxColumnSelector = hasColumns && (
    <div className="adm-col-selector" ref={columnMenuRef}>
      <button
        type="button"
        className={`adm-table-toolbar__btn adm-col-selector__trigger${showColumnMenu ? ' adm-col-selector__trigger--open' : ''}`}
        onClick={() => setShowColumnMenu(v => !v)}
        title="Show / hide columns"
      >
        <i className="fa fa-columns" aria-hidden="true" />
        <span className="adm-table-toolbar__btn-label">Columns</span>
        {visibleCount < totalCount && (
          <span className="adm-col-selector__badge">{visibleCount}/{totalCount}</span>
        )}
        <i
          className={`fa fa-chevron-${showColumnMenu ? 'up' : 'down'} adm-col-selector__chevron`}
          aria-hidden="true"
        />
      </button>

      {showColumnMenu && (
        <div className="adm-col-selector__menu" role="menu">
          <div className="adm-col-selector__menu-header">
            Columns&nbsp;
            <span className="adm-col-selector__menu-count">
              {visibleCount} / {totalCount} shown
            </span>
          </div>
          <div className="adm-col-selector__menu-actions">
            <button
              type="button"
              className="adm-col-selector__menu-action-btn"
              onClick={() => typeof onShowAllColumns === 'function' && onShowAllColumns()}
            >
              Show all
            </button>
            <button
              type="button"
              className="adm-col-selector__menu-action-btn"
              onClick={() => typeof onHideAllColumns === 'function' && onHideAllColumns()}
            >
              Hide all
            </button>
            <button
              type="button"
              className="adm-col-selector__menu-action-btn adm-col-selector__menu-action-btn--muted"
              onClick={() => typeof onResetColumns === 'function' && onResetColumns()}
            >
              Reset
            </button>
          </div>
          <ul className="adm-col-selector__list">
            {columns.map(col => (
              <li key={col.key} className="adm-col-selector__item">
                <label className="adm-col-selector__label">
                  <input
                    type="checkbox"
                    className="adm-col-selector__checkbox"
                    checked={col.visible !== false}
                    onChange={() => typeof onToggleColumn === 'function' && onToggleColumn(col.key)}
                  />
                  <span className="adm-col-selector__col-name">{col.title || col.key}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const jsxActions = (
    <>
      {typeof onCreateClick === 'function' && (
        <button
          type="button"
          className="adm-table-toolbar__btn adm-table-toolbar__btn--primary"
          onClick={onCreateClick}
          title="Create new item"
        >
          <i className="fa fa-plus" aria-hidden="true" />
          <span className="adm-table-toolbar__btn-label">Create (Current)</span>
        </button>
      )}
      {typeof onCreateAltClick === 'function' && (
        <button
          type="button"
          className="adm-table-toolbar__btn adm-table-toolbar__btn--alt-create"
          onClick={onCreateAltClick}
          title="Create new item (new UX)"
        >
          <i className="fa fa-magic" aria-hidden="true" />
          <span className="adm-table-toolbar__btn-label">{createAltLabel || 'Create (New UX)'}</span>
        </button>
      )}
      {typeof onRefreshClick === 'function' && (
        <button
          type="button"
          className="adm-table-toolbar__btn adm-table-toolbar__btn--icon"
          onClick={onRefreshClick}
          title="Refresh data"
        >
          <i className="fa fa-refresh" aria-hidden="true" />
        </button>
      )}
    </>
  );

  if (twoRows) {
    return (
      <div className="adm-table-toolbar adm-table-toolbar--two-rows">
        {/* Row 1: search + column picker + actions */}
        <div className="adm-table-toolbar__row-1">
          <div className="adm-table-toolbar__left">
            {jsxSearch}
            {jsxSearchRowExtra && (
              <div className="adm-table-toolbar__search-row-extra">
                {jsxSearchRowExtra}
              </div>
            )}
          </div>
          <div className="adm-table-toolbar__right">
            {jsxColumnSelector}
            {jsxActions}
          </div>
        </div>

        {/* Row 2: contextual filters */}
        <div className="adm-table-toolbar__row-2">
          <div className="adm-table-toolbar__filters">
            {jsxFilters}
          </div>
        </div>
      </div>
    );
  }

  // Single-row layout (no filters)
  return (
    <div className="adm-table-toolbar">
      <div className="adm-table-toolbar__left">
        {jsxSearch}
        {jsxSearchRowExtra && (
          <div className="adm-table-toolbar__search-row-extra">
            {jsxSearchRowExtra}
          </div>
        )}
      </div>
      <div className="adm-table-toolbar__right">
        {jsxColumnSelector}
        {jsxActions}
      </div>
    </div>
  );
};

export default AdminTableToolbar;
